import React, { useMemo, useState } from 'react';
import { submitEstimate } from '../api/client';

const STEP_WELCOME = 0;
const STEP_SQFT = 1;
const STEP_PAINT = 2;
const STEP_ADDONS = 3;
const STEP_CONDITION = 4;
const STEP_CONTACT = 5;
const STEP_CONSENT = 6;
const STEP_LOADING = 7;
const STEP_RESULT = 8;

const TOTAL_QUESTIONS = 6;

const PAINT_CHOICES = [
  {
    value: 'a100',
    label: 'Budget (A-100)',
    rate: '$1.35 / sqft',
    description: 'Solid coverage for rental or budget projects.'
  },
  {
    value: 'superpaint',
    label: 'Standard (SuperPaint)',
    rate: '$1.55 / sqft',
    description: 'Our most popular option for durability and value.',
    highlight: 'Most Popular'
  },
  {
    value: 'latitude',
    label: 'Premium (Latitude)',
    rate: '$1.80 / sqft',
    description: 'Extra UV protection with premium finish.'
  },
  {
    value: 'emerald',
    label: 'Ultimate (Emerald Rain Refresh)',
    rate: '$2.00 / sqft',
    description: 'Top-tier performance with a rich, lasting finish.'
  }
];

const ADDON_CHOICES = [
  {
    key: 'lightColor',
    label: 'Switching from Dark to Light/Bright Colors',
    description: 'Primer work for lighter color transitions (+15%).'
  },
  {
    key: 'twoToneGutters',
    label: 'Gutters Two Tone',
    description: 'Gutters a different color than the house (+$250).'
  },
  {
    key: 'accentWork',
    label: 'Accent Areas',
    description: 'Doors, shutters, or accent trim (+$250).'
  },
  {
    key: 'columnsPillars',
    label: 'Pillars/Columns',
    description: 'Paint exterior columns or porch pillars (+$250).'
  }
];

const CONDITION_CHOICES = [
  {
    value: 'excellent',
    label: 'Excellent',
    description: 'Well maintained with little to no peeling.'
  },
  {
    value: 'good',
    label: 'Good',
    description: 'Minor wear with small touch-ups needed.'
  },
  {
    value: 'fair',
    label: 'Fair',
    description: 'Some peeling or small cracks visible.'
  },
  {
    value: 'needs_attention',
    label: 'Needs attention',
    description: 'Visible damage or repairs needed.'
  }
];

const REPAIR_LABELS = {
  stucco_heavy_cracking: 'Stucco heavy cracking',
  stucco_severe_repair: 'Stucco severe repair',
  mold_severe: 'Mold removal',
  rust_peeling: 'Rust or peeling areas',
  drywall_repair: 'Drywall repair',
  accents_full_day: 'Accent prep (full day)'
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatNumber = (value) => {
  if (!Number.isFinite(value)) return '';
  return new Intl.NumberFormat('en-US').format(value);
};

const formatCurrency = (value) => {
  if (!Number.isFinite(value)) return '';
  return `$${new Intl.NumberFormat('en-US').format(value)}`;
};

const getProgressStep = (step) => {
  if (step <= STEP_WELCOME) return 0;
  if (step <= STEP_CONSENT) return step;
  return TOTAL_QUESTIONS;
};

export default function CalculatorView() {
  const [step, setStep] = useState(STEP_WELCOME);
  const [errors, setErrors] = useState([]);
  const [apiError, setApiError] = useState('');
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    livableSqft: '',
    paintQuality: '',
    lightColor: false,
    twoToneGutters: false,
    accentWork: false,
    columnsPillars: false,
    homeCondition: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    projectNotes: '',
    consent: false
  });

  const progressStep = getProgressStep(step);

  const summaryLine = useMemo(() => {
    const parts = [];
    const sqftNumber = Number(form.livableSqft);
    if (Number.isFinite(sqftNumber) && sqftNumber > 0) {
      parts.push(`${formatNumber(sqftNumber)} sq ft`);
    }
    const paintChoice = PAINT_CHOICES.find((choice) => choice.value === form.paintQuality);
    if (paintChoice) {
      parts.push(paintChoice.label);
    }
    return parts.join(' • ');
  }, [form.livableSqft, form.paintQuality]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validateStep = (currentStep) => {
    const stepErrors = [];
    if (currentStep === STEP_SQFT) {
      const sqftValue = Number(form.livableSqft);
      if (!Number.isFinite(sqftValue) || sqftValue < 100) {
        stepErrors.push('Enter a livable square footage of at least 100.');
      }
    }
    if (currentStep === STEP_PAINT) {
      if (!form.paintQuality) {
        stepErrors.push('Select a paint quality to continue.');
      }
    }
    if (currentStep === STEP_CONDITION) {
      if (!form.homeCondition) {
        stepErrors.push('Select the condition that best matches your home.');
      }
    }
    if (currentStep === STEP_CONTACT) {
      if (!form.customerName.trim()) {
        stepErrors.push('Name is required.');
      }
      if (!form.customerEmail.trim() || !emailRegex.test(form.customerEmail)) {
        stepErrors.push('Valid email is required.');
      }
      if (!form.customerPhone.trim()) {
        stepErrors.push('Phone number is required.');
      }
      if (!form.customerAddress.trim()) {
        stepErrors.push('Address is required.');
      }
    }
    if (currentStep === STEP_CONSENT) {
      if (!form.consent) {
        stepErrors.push('Consent is required to show your estimate.');
      }
    }

    return stepErrors;
  };

  const handleNext = async () => {
    setApiError('');
    const stepErrors = validateStep(step);
    if (stepErrors.length) {
      setErrors(stepErrors);
      return;
    }

    setErrors([]);

    if (step === STEP_CONSENT) {
      await handleSubmit();
      return;
    }

    setStep((prev) => Math.min(prev + 1, STEP_RESULT));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setStep(STEP_LOADING);

    const payload = {
      livableSqft: Number(form.livableSqft),
      paintQuality: form.paintQuality,
      lightColor: Boolean(form.lightColor),
      twoToneGutters: Boolean(form.twoToneGutters),
      accentWork: Boolean(form.accentWork),
      columnsPillars: Boolean(form.columnsPillars),
      homeCondition: form.homeCondition,
      customerName: form.customerName.trim(),
      customerEmail: form.customerEmail.trim(),
      customerPhone: form.customerPhone.trim(),
      customerAddress: form.customerAddress.trim(),
      projectNotes: form.projectNotes.trim(),
      consent: Boolean(form.consent)
    };

    try {
      const response = await submitEstimate(payload);
      setResult(response);
      setStep(STEP_RESULT);
    } catch (error) {
      setApiError(error.message || 'Unable to calculate estimate.');
      setStep(STEP_CONSENT);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step <= STEP_WELCOME || step === STEP_LOADING || step === STEP_RESULT) {
      return;
    }
    setErrors([]);
    setApiError('');
    setStep((prev) => Math.max(prev - 1, STEP_WELCOME));
  };

  const handleStartOver = () => {
    setForm({
      livableSqft: '',
      paintQuality: '',
      lightColor: false,
      twoToneGutters: false,
      accentWork: false,
      columnsPillars: false,
      homeCondition: '',
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      customerAddress: '',
      projectNotes: '',
      consent: false
    });
    setErrors([]);
    setApiError('');
    setResult(null);
    setStep(STEP_WELCOME);
  };

  const renderProgress = () => {
    if (step === STEP_WELCOME) return null;
    return (
      <div className="calc-progress">
        <span className="calc-progress-text">Step {progressStep} of {TOTAL_QUESTIONS}</span>
        <div className="calc-progress-dots">
          {Array.from({ length: TOTAL_QUESTIONS }).map((_, index) => (
            <span
              key={`dot-${index}`}
              className={`calc-dot ${index < progressStep ? 'active' : ''}`}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderErrors = () => {
    if (!errors.length && !apiError) return null;
    return (
      <div className="calc-error">
        {apiError && <p>{apiError}</p>}
        {errors.map((message) => (
          <p key={message}>{message}</p>
        ))}
      </div>
    );
  };

  const renderWelcome = () => (
    <div className="calc-step">
      <div className="calc-kicker">Free exterior painting estimate</div>
      <h1>Get a clear range in just a few questions.</h1>
      <p className="calc-subtitle">
        Answer a quick set of questions and we will show your estimate range instantly.
      </p>
      <div className="calc-actions">
        <button className="calc-primary" onClick={() => setStep(STEP_SQFT)}>
          Get my estimate
        </button>
        <a className="calc-link" href="/">Staff login</a>
      </div>
    </div>
  );

  const renderSqft = () => (
    <div className="calc-step">
      <h2>What is your livable square footage?</h2>
      <p className="calc-hint">Minimum 100 sq ft. We estimate paintable area as livable sqft + 800–1,400.</p>
      <input
        type="number"
        inputMode="numeric"
        className="calc-input"
        placeholder="e.g. 2500"
        value={form.livableSqft}
        onChange={(event) => updateField('livableSqft', event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            handleNext();
          }
        }}
        min="100"
      />
      {renderErrors()}
      <div className="calc-actions">
        <button className="calc-primary" onClick={handleNext}>
          Continue
        </button>
        <button className="calc-secondary" onClick={handleBack}>
          Back
        </button>
      </div>
    </div>
  );

  const renderPaint = () => (
    <div className="calc-step">
      <h2>Choose your paint quality</h2>
      <p className="calc-hint">Select the finish that best matches your goals.</p>
      <div className="calc-grid">
        {PAINT_CHOICES.map((choice) => (
          <button
            type="button"
            key={choice.value}
            className={`calc-choice ${form.paintQuality === choice.value ? 'selected' : ''} ${choice.highlight ? 'has-badge' : ''}`}
            onClick={() => {
              updateField('paintQuality', choice.value);
              setErrors([]);
              setTimeout(() => setStep(STEP_ADDONS), 120);
            }}
          >
            {choice.highlight && <span className="calc-badge">{choice.highlight}</span>}
            <h3>{choice.label}</h3>
            <p className="calc-rate">{choice.rate}</p>
            <p className="calc-choice-text">{choice.description}</p>
          </button>
        ))}
      </div>
      {renderErrors()}
      <div className="calc-actions">
        <button className="calc-secondary" onClick={handleBack}>
          Back
        </button>
      </div>
    </div>
  );

  const renderAddOns = () => (
    <div className="calc-step">
      <h2>Any add-ons we should include?</h2>
      <p className="calc-hint">Select all that apply.</p>
      <div className="calc-grid calc-grid-tight">
        {ADDON_CHOICES.map((choice) => (
          <button
            type="button"
            key={choice.key}
            className={`calc-choice ${form[choice.key] ? 'selected' : ''}`}
            onClick={() => updateField(choice.key, !form[choice.key])}
          >
            <h3>
              {choice.key === 'lightColor' ? (
                <span className="calc-tone-label">
                  Switching from <span className="calc-tone-dark">Dark</span> to{' '}
                  <span className="calc-tone-light">Light/Bright</span> Colors
                </span>
              ) : (
                choice.label
              )}
            </h3>
            <p className="calc-choice-text">{choice.description}</p>
          </button>
        ))}
      </div>
      <div className="calc-actions">
        <button className="calc-primary" onClick={handleNext}>
          Continue
        </button>
        <button className="calc-secondary" onClick={handleBack}>
          Back
        </button>
      </div>
    </div>
  );

  const renderCondition = () => (
    <div className="calc-step">
      <h2>How would you describe your home's condition?</h2>
      <p className="calc-hint">This helps us estimate potential prep and repairs.</p>
      <div className="calc-grid calc-grid-tight">
        {CONDITION_CHOICES.map((choice) => (
          <button
            type="button"
            key={choice.value}
            className={`calc-choice ${form.homeCondition === choice.value ? 'selected' : ''}`}
            onClick={() => {
              updateField('homeCondition', choice.value);
              setErrors([]);
            }}
          >
            <h3>{choice.label}</h3>
            <p className="calc-choice-text">{choice.description}</p>
          </button>
        ))}
      </div>
      {renderErrors()}
      <div className="calc-actions">
        <button className="calc-primary" onClick={handleNext}>
          Continue
        </button>
        <button className="calc-secondary" onClick={handleBack}>
          Back
        </button>
      </div>
    </div>
  );

  const renderContact = () => (
    <div className="calc-step">
      <h2>Where should we send your estimate?</h2>
      <p className="calc-hint">We will only use this to follow up on your estimate.</p>
      <div className="calc-grid calc-grid-form">
        <div className="calc-field">
          <label>Name</label>
          <input
            className="calc-input"
            type="text"
            value={form.customerName}
            onChange={(event) => updateField('customerName', event.target.value)}
          />
        </div>
        <div className="calc-field">
          <label>Email</label>
          <input
            className="calc-input"
            type="email"
            value={form.customerEmail}
            onChange={(event) => updateField('customerEmail', event.target.value)}
          />
        </div>
        <div className="calc-field">
          <label>Phone</label>
          <input
            className="calc-input"
            type="tel"
            value={form.customerPhone}
            onChange={(event) => updateField('customerPhone', event.target.value)}
          />
        </div>
        <div className="calc-field">
          <label>Address</label>
          <input
            className="calc-input"
            type="text"
            value={form.customerAddress}
            onChange={(event) => updateField('customerAddress', event.target.value)}
          />
        </div>
        <div className="calc-field calc-field-full">
          <label>Project notes (optional)</label>
          <textarea
            className="calc-input calc-textarea"
            rows="3"
            value={form.projectNotes}
            onChange={(event) => updateField('projectNotes', event.target.value)}
          />
        </div>
      </div>
      {renderErrors()}
      <div className="calc-actions">
        <button className="calc-primary" onClick={handleNext}>
          Continue
        </button>
        <button className="calc-secondary" onClick={handleBack}>
          Back
        </button>
      </div>
    </div>
  );

  const renderConsent = () => (
    <div className="calc-step">
      <h2>Quick consent before we show your estimate</h2>
      <p className="calc-hint">
        By continuing you agree to let JSJ Painting use your information to prepare an estimate and contact you.
      </p>
      <label className="calc-consent">
        <input
          type="checkbox"
          checked={form.consent}
          onChange={(event) => updateField('consent', event.target.checked)}
        />
        <span>I agree and want to see my estimate.</span>
      </label>
      {renderErrors()}
      <div className="calc-actions">
        <button className="calc-primary" onClick={handleNext} disabled={submitting}>
          {submitting ? 'Working...' : 'Show my estimate'}
        </button>
        <button className="calc-secondary" onClick={handleBack}>
          Back
        </button>
      </div>
    </div>
  );

  const renderLoading = () => (
    <div className="calc-step calc-loading">
      <h2>Calculating your estimate...</h2>
      <div className="calc-loader" />
      <p className="calc-hint">This usually takes just a few seconds.</p>
    </div>
  );

  const renderResult = () => {
    if (!result) return null;
    const estimate = result.estimate || {};
    const breakdown = result.breakdown || {};
    const addOns = breakdown.addOns || {};
    const repairItems = breakdown.repairItems || {};
    const baseEstimate = estimate.min;
    const repairAllowance = Number.isFinite(breakdown.repairAllowance)
      ? breakdown.repairAllowance
      : breakdown.repairMaxTotal;
    const worstCase = estimate.max;
    const conditionChoice = CONDITION_CHOICES.find((choice) => choice.value === breakdown.condition || choice.value === form.homeCondition);

    return (
      <div className="calc-step calc-result">
        <div className="calc-result-header">
          <h2>Your estimate</h2>
          <div className="calc-estimate-hero">
            <div className="calc-estimate-card calc-estimate-base">
              <div className="calc-estimate-label">Estimated base</div>
              <div className="calc-estimate-value">{formatCurrency(baseEstimate)}</div>
              <p className="calc-estimate-sub">Based on your inputs.</p>
            </div>
          </div>
          {estimate.note && <p className="calc-note">{estimate.note}</p>}
        </div>
        <details className="calc-collapsible">
          <summary>Potential Additional Costs</summary>
          <p className="calc-collapsible-hint">Tap to view repair scenarios.</p>
          <div className="calc-estimate-tiers">
            <div className="calc-estimate-card">
              <div className="calc-estimate-label">Potential additional costs</div>
              <div className="calc-estimate-value">{formatCurrency(repairAllowance)}</div>
              <p className="calc-estimate-sub">
                Based on your condition: {conditionChoice ? conditionChoice.label : 'Not specified'}.
              </p>
            </div>
            <div className="calc-estimate-card calc-estimate-total">
              <div className="calc-estimate-label">Estimate if repairs are needed</div>
              <div className="calc-estimate-value">{formatCurrency(worstCase)}</div>
            </div>
          </div>
          <div className="calc-result-grid calc-result-grid-tight">
            <div className="calc-panel">
              <h3>Repair details (if needed)</h3>
              <ul>
                {Object.entries(repairItems).map(([key, value]) => (
                  <li key={key}>{REPAIR_LABELS[key] || key}: {formatCurrency(value)}</li>
                ))}
              </ul>
            </div>
          </div>
        </details>
        <div className="calc-result-grid">
          <div className="calc-panel">
            <h3>Estimate summary</h3>
            <ul>
              <li>Base price: {formatCurrency(breakdown.basePrice)}</li>
              <li>Add-ons: {formatCurrency(addOns.total)}</li>
              <li>Paintable sqft: {formatNumber(breakdown.paintableSqft)}</li>
              {conditionChoice && <li>Condition: {conditionChoice.label}</li>}
            </ul>
          </div>
        </div>
        <div className="calc-result-footer">
          {result.leadId && <p className="calc-reference">Reference ID: {result.leadId}</p>}
          <div className="calc-actions">
            <button className="calc-secondary" onClick={handleStartOver}>
              Start over
            </button>
            <a className="calc-primary" href="/">
              Go to staff portal
            </a>
          </div>
        </div>
      </div>
    );
  };

  const renderStep = () => {
    switch (step) {
      case STEP_WELCOME:
        return renderWelcome();
      case STEP_SQFT:
        return renderSqft();
      case STEP_PAINT:
        return renderPaint();
      case STEP_ADDONS:
        return renderAddOns();
      case STEP_CONDITION:
        return renderCondition();
      case STEP_CONTACT:
        return renderContact();
      case STEP_CONSENT:
        return renderConsent();
      case STEP_LOADING:
        return renderLoading();
      case STEP_RESULT:
        return renderResult();
      default:
        return renderWelcome();
    }
  };

  return (
    <div className="calculator-page">
      <div className="calc-shell">
        <header className="calc-header">
          <div className="calc-logo">
            <span className="calc-logo-mark" />
            JSJ Painting
          </div>
          <div className="calc-header-links">
            {summaryLine && <span className="calc-summary">{summaryLine}</span>}
          </div>
        </header>
        {renderProgress()}
        <section className="calc-card">
          {renderStep()}
        </section>
        <footer className="calc-footer">
          <p>Estimate ranges are approximate. Final quote comes after an on-site review.</p>
        </footer>
      </div>
    </div>
  );
}
